/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {Preference} from '@ion/core/src/types/preferences';
import {isSheetTableView} from '@ion/dls/src/components/sidesheet/helpers';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {SideSheetView} from '@ion/dls/src/types/types';
import {setSideSheetView} from '../../../../store/actions/tracking';
import {
  findNearbySelector,
  isLargeFleetModeSelector,
  sideSheetViewSelector
} from '../../../../store/selectors/tracking-selectors';

/**
 *
 * React Hook to provide util functions for saving asset view mode in sidesheet.
 *
 *  ```typescript
 *
 *   import {useSaveAssetViewMode} from '@/routes/tracking/hooks';
 *
 *   const [viewMode, setViewMode, saveViewMode] = useSaveAssetViewMode();
 *
 *  ```
 */
export const useSaveAssetViewMode = () => {
  const dispatch           = useDispatch();
  const {isDesktop}        = useMedia();
  const isLargeFleetMode   = useSelector(isLargeFleetModeSelector);
  const findLabel          = useSelector(findNearbySelector);
  const sideSheetView      = useSelector(sideSheetViewSelector);
  const viewModePreference = UserPreferences.get(Preference.trackingAssetViewMode);

  const setViewMode = useCallback((view: SideSheetView) => {
    UserPreferences.set(Preference.trackingAssetViewMode, view, true);
    dispatch(setSideSheetView(view));
  }, []);

  const saveViewMode = useCallback(() => {
    const viewMode = UserPreferences.get(Preference.trackingAssetViewMode);
    if (viewMode) {
      const view = (isLargeFleetMode && !isDesktop) && isSheetTableView(viewMode)
        ? SideSheetView.detailed : viewMode;
      dispatch(setSideSheetView(view));
    }
  }, [isDesktop, isLargeFleetMode]);
  const viewMode = (findLabel?.miles && findLabel?.type)
    ? sideSheetView
    : viewModePreference;
  return [viewMode, setViewMode, saveViewMode];
};

/**
 *
 * Narrow companion to useSaveAssetViewMode() for callers that only need
 * the setViewMode setter, without subscribing to the selectors
 * useSaveAssetViewMode() itself reads for its other return values.
 *
 *  ```typescript
 *
 *   import {useSaveAssetViewModeSetter} from '@/routes/tracking/hooks';
 *
 *   const setViewMode = useSaveAssetViewModeSetter();
 *
 *  ```
 */
export const useSaveAssetViewModeSetter = () => {
  const dispatch = useDispatch();

  return useCallback((view: SideSheetView) => {
    UserPreferences.set(Preference.trackingAssetViewMode, view, true);
    dispatch(setSideSheetView(view));
  }, []);
};
